"use client";

import { RetrievedBlogCategory, RetrievedBlogPostsListItem } from "@/lib/sanity/responseTypes";
import { useEffect, useState } from "react";
import PageLayout from "../PageLayout";
import Title from "../Title";
import NavigationFilters from "../NavigationFilters";
import { useTranslations } from "next-intl";
import PostGallery from "./post-gallery";
import { ArticleInfo } from "@/types";

import ArrowDown from "@/components/icons/arrow-down.svg";
import { Link } from "@/i18n/navigation";
import HeadingLarge from "../text/heading-large";
import { ArrowLeft } from "lucide-react";

interface MagazineProps {
  locale: string;
  categories: RetrievedBlogCategory[];
  posts: ArticleInfo[];
  currentCategory?: RetrievedBlogCategory;
}

export default function Magazine(props: MagazineProps) {
  const locale = props.locale;
  const t = useTranslations('Blog');
  const [articles, setArticles] = useState<ArticleInfo[]>(props.posts);
  const [articlesCategories, setArtictlesCategories] = useState<RetrievedBlogCategory[]>(props.categories);
  const [articlesSliceEnd, setArticlesSliceEnd] = useState<number>(10);
  const [articlesSlice, setArticlesSlice] = useState<ArticleInfo[]>(props.posts.slice(0, 10));

  const [navigationFiltersItems, setNavigationFiltersItems] = useState<{
    label: string,
    doNotTranslate?: boolean,
    active: boolean,
    path: string,
  }[]>([]);

  useEffect(() => {
    setArticlesSlice(articles.slice(0, articlesSliceEnd));
  }, [articles, articlesSliceEnd]);

  useEffect(() => {
    let filterItems = [
      { label: "all-posts", active: !props.currentCategory, path: "/blog" },
    ]
    let filterItemsCategories = articlesCategories.map((p, i) => {
      let relevantName = p.title[locale];
      return {
        label: relevantName,
        doNotTranslate: true,
        active: props.currentCategory && props.currentCategory.slug === p.slug || false,
        path: `/blog/kategorie/${p.slug}`,
      };
    })

    filterItems = filterItems.concat(filterItemsCategories);
    setNavigationFiltersItems(filterItems);
  }, [locale, articlesCategories]);

  function loadMore() {
    setArticlesSliceEnd(articlesSliceEnd + 10);
  }

  function hasMore() {
    return articlesSliceEnd < articles.length;
  }

  let relevantBreadcrumbs: { title: string, path: string, doNotTranslate?: boolean }[];
  let relevantTitle: string;
  if (props.currentCategory) {
    relevantBreadcrumbs = [{ title: t('title'), path: '/blog', doNotTranslate: true }]
    relevantTitle = props.currentCategory.title[locale];
  } else {
    relevantBreadcrumbs = []; //Besides home, nothing to add
    relevantTitle = t('title');
  }

  return (
    <PageLayout padded={true} gapPx={20}>
      <Title breadcrumbs={relevantBreadcrumbs} title={relevantTitle}></Title>
      <NavigationFilters items={navigationFiltersItems} translationCollection="Blog" />
      {props.posts.length === 0 && (
        <div className="flex flex-col w-full h-[150px] md:h-[500px] gap-6 py-16 md:py-64 items-center justify-center">
          <HeadingLarge>{t('no-posts')}</HeadingLarge>
          <div className="flex flex-row justify-center">
            <Link href="/blog">
              <button className="btn btn-secondary rounded-full text-white">
                <ArrowLeft className="h-[18px] w-[18px]  stroke-white" />
                {t('all-posts')}
              </button>
            </Link>
          </div>
        </div>
      )}
      {props.posts.length > 0 && (
        <>
          {/* Desktop version */}
          <div className="hidden md:flex flex-col gap-5">
            <div className="w-full flex flex-col md:flex-row gap-[30px]">
              <Link href={articlesSlice[0].articlePageUrl} className="flex-1">
                <img src={`${articlesSlice[0].imgSrc}?w=${645 * 2}&h=${430 * 2}`} className={`aspect-3/2 rounded-4xl w-full max-h-full object-cover`} />
              </Link>
              <div className="w-full flex-1 flex flex-col gap-4 items-center justify-center">
                <Link href={articlesSlice[0].articlePageUrl} className="text-primary font-black text-2xl md:text-5xl leading-[120%] font-[Outfit]">
                  {articlesSlice[0].title}
                </Link>
                <Link href={articlesSlice[0].articlePageUrl} className="text-primary text-lg md:text-2xl leading-[120%] font-[Outfit]">
                  {articlesSlice[0].intro}
                </Link>
              </div>
            </div>
            <PostGallery articles={articlesSlice.slice(1)} itemsPerRow={3} itemWidth={400} imageHeight={275} gapX={60} readMoreLabel={t('show-more')} showMagazineLink={false} showSummaries={true} />
          </div>
          {/* Mobile version */}
          <div className="flex md:hidden flex-col gap-5">
            <PostGallery articles={articlesSlice} itemsPerRow={3} itemWidth={400} imageHeight={275} gapX={60} readMoreLabel={t('show-more')} showMagazineLink={false} showSummaries={true} />
          </div>
          {hasMore() && (<div className="w-full flex flex-row relative overflow-hidden items-center justify-center p-5 box-border">
            <button className="btn btn-secondary text-white rounded-full gap-1.5" onClick={loadMore}>
              {t('show-more')}
              <ArrowDown className="w-3.5 h-3.5 stroke-white" />
            </button>
          </div>)}
        </>
      )}

    </PageLayout>
  )
}
